<template>
    <div class="pa-4">
        <div class="text-center mb-4">
            <v-icon size="36" color="primary" class="mb-2">mdi-account-group</v-icon>
            <h3 class="text-subtitle-1 font-weight-bold">Notification Recipients</h3>
            <p class="text-body-2 text-medium-emphasis">Choose who gets notified when an alert is triggered</p>
        </div>

        <div class="d-flex justify-end mb-3">
            <v-btn color="primary" size="small" variant="tonal" prepend-icon="mdi-plus"
                   @click="createRecipient">
                Add Recipient
            </v-btn>
        </div>

        <v-card variant="outlined" rounded="lg">
            <v-table density="compact" hover>
                <thead>
                <tr>
                    <th>Recipient</th>
                    <th class="text-center" style="width: 60px">App</th>
                    <th class="text-center" style="width: 60px">SMS</th>
                    <th class="text-center" style="width: 60px">Email</th>
                    <th class="text-center" style="width: 100px">Actions</th>
                </tr>
                </thead>
                <tbody>
                <template v-if="loading">
                    <tr>
                        <td colspan="5" class="text-center pa-6">
                            <v-progress-circular color="primary" indeterminate size="24"/>
                            <p class="text-body-2 text-medium-emphasis mt-2 mb-0">Loading recipients...</p>
                        </td>
                    </tr>
                </template>
                <template v-else-if="members.length">
                    <tr v-for="member in members" :key="member.id" class="text-left">
                        <td>
                            <div class="d-flex align-center ga-2">
                                <v-avatar size="28" :color="member.type === 0 ? 'primary' : 'grey'" variant="tonal">
                                    <v-icon size="16">{{ member.type === 0 ? 'mdi-account' : 'mdi-at' }}</v-icon>
                                </v-avatar>
                                <span class="text-body-2">{{
                                    member.type === 0
                                        ? `${member.first_name} ${member.last_name}`
                                        : (member.email_enabled ? member.email : member.sms)
                                }}</span>
                            </div>
                        </td>
                        <td class="text-center">
                            <v-icon size="18" :color="member?.app_enabled ? 'success' : 'grey-lighten-1'"
                                    :icon="member?.app_enabled ? 'mdi-check-circle' : 'mdi-minus-circle-outline'"/>
                        </td>
                        <td class="text-center">
                            <v-icon size="18" :color="member?.sms_enabled ? 'success' : 'grey-lighten-1'"
                                    :icon="member?.sms_enabled ? 'mdi-check-circle' : 'mdi-minus-circle-outline'"/>
                        </td>
                        <td class="text-center">
                            <v-icon size="18" :color="member?.email_enabled ? 'success' : 'grey-lighten-1'"
                                    :icon="member?.email_enabled ? 'mdi-check-circle' : 'mdi-minus-circle-outline'"/>
                        </td>
                        <td class="text-center">
                            <v-btn icon="mdi-pencil-outline" size="x-small" variant="text" color="primary"
                                   :disabled="editDialogSwitch" @click="editRecipient(member)"/>
                            <v-btn icon="mdi-delete-outline" size="x-small" variant="text"
                                   color="error" @click="deleteRecipient(member)"/>
                        </td>
                    </tr>
                </template>
                <template v-else>
                    <tr>
                        <td colspan="5" class="text-center pa-6">
                            <v-icon size="36" color="grey-lighten-1" class="mb-2">mdi-account-plus-outline</v-icon>
                            <p class="text-body-2 text-medium-emphasis mb-2">No recipients added yet</p>
                            <v-btn size="small" variant="tonal" color="primary" prepend-icon="mdi-plus"
                                   @click="createRecipient">Add Recipient</v-btn>
                        </td>
                    </tr>
                </template>
                </tbody>
            </v-table>
        </v-card>
    </div>

    <Create-Recipient v-if="createDialogSwitch" v-model="createDialogSwitch" :group="group"
                      :existing-user-ids="existingUserIds" @addRecipient="insertRecipient"/>
    <Edit-Recipient v-if="editDialogSwitch" v-model="editDialogSwitch" :group="group" :recipient="selectedRecipient"
                    @editMember="updateRecipient"/>
</template>

<script setup>
import {ref, computed, onMounted} from "vue";
import {send} from "@/composables/apiClient";
import {useSnackbarStore} from "@/stores/snackBarStore";
import EditRecipient from "@/components/dialogs/monitoring/recipients/Edit.vue";
import CreateRecipient from "@/components/dialogs/monitoring/recipients/Create.vue";

const group = ref(null);
const loading = ref(true);
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const selectedRecipient = ref(null);
const {showSnackbar} = useSnackbarStore();
const props = defineProps({
    group: Number,
});
const emits = defineEmits(['updateNotificationGroup']);
const members = ref([]);
// Circle users already in the group, so the Add dialog doesn't offer them again.
const existingUserIds = computed(() => members.value.filter((m) => m.user_id).map((m) => m.user_id));

onMounted(() => {
    fetchRecipients();
});

const fetchRecipients = () => {
    send('get', `monitoring/single-group-members${props.group ? '/' + props.group : ''}`)
        .then((response) => {
            group.value = response.data.data.group;
            members.value = response.data.data.members;
            emits('updateNotificationGroup', group.value.id);
        })
        .catch(() => {
            showSnackbar('Recipients could not be loaded', 'error');
        })
        .finally(() => {
            loading.value = false;
        });
};

const createRecipient = () => {
    createDialogSwitch.value = true;
};

const insertRecipient = (recipient) => {
    members.value.unshift(recipient);
};

const editRecipient = (recipient) => {
    selectedRecipient.value = recipient;
    editDialogSwitch.value = true;
};

const updateRecipient = (recipient) => {
    const index = members.value.findIndex(r => r.id === recipient.id);
    if (index !== -1) members.value[index] = recipient;
};

const deleteRecipient = (recipient) => {
    if (confirm('Are you sure you want to remove this recipient?')) {
        send('delete', `monitoring/notification-group-members/${recipient.id}`)
            .then((response) => {
                if (response.data.success) {
                    members.value = members.value.filter(m => m.id !== recipient.id);
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                showSnackbar(error.response?.data?.message ?? 'Recipient could not be deleted', 'error');
            });
    }
};
</script>
